import { useI18n } from "@wojo/localization";
import { LoadingSkeleton, Text } from "@wojo/ui";
import pluralize from "pluralize";
import React, { FC, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTourPageContext } from "../../_client/context";
import { TourHeroLeadInPrice } from "./TourHeroLeadInPrice";
import styles from "./TourHeroSummary.module.scss";
import { TourHeroSummaryButtons } from "./TourHeroSummaryButtons";
import { TourHeroSummaryInfo } from "./TourHeroSummaryInfo";

export const TourHeroSummary: FC<React.PropsWithChildren<unknown>> = () => {
    const { tourContent, colorPalette, pageConfig } = useTourPageContext();
    const extension = tourContent.extension;
    const extensionName = extension?.name;
    const isExtensionHidden = extension?.isHidden;
    const titleColor = `var(--s-color-${colorPalette}-bold)`;
    const { productPage } = useI18n("productPage");
    const tourName = tourContent.name;

    return (
        <div className={styles["tour-hero-summary-wrapper"]}>
            <div>
                <Text.Heading
                    tag="h1"
                    order="2"
                    title={tourContent.tourCode ?? ""}
                    className={styles.heading}
                    style={{
                        color: titleColor,
                    }}
                >
                    {tourName}
                </Text.Heading>
                <Text>
                    {!!tourContent.nbDays &&
                        productPage.tourHero.heroSummary.numberOfDays.l({
                            nbDays: `${tourContent.nbDays}`,
                        })}
                    {!!tourContent.nbCities && (
                        <>
                            {","}
                            &nbsp;
                            {`${tourContent.nbCities} `}
                            {`${pluralize(
                                productPage.tourHero.heroSummary.placesUnit.l(),
                                tourContent.nbCities,
                            )}`}
                        </>
                    )}
                    {extensionName && !isExtensionHidden && (
                        <>
                            &nbsp;&nbsp;|&nbsp;&nbsp;
                            {productPage.tourHero.heroSummary.numberOfDaysWithExtension.l(
                                {
                                    nbDays: `${tourContent.nbDaysWithExtension}`,
                                    extensionName: `${extensionName}`,
                                },
                            )}
                        </>
                    )}
                    {(pageConfig.displayLowestPayment ||
                        pageConfig.displayLowestLeadInPrice) && (
                        <>
                            <ErrorBoundary fallback={null}>
                                <Suspense
                                    fallback={
                                        <>
                                            &nbsp;&nbsp;|&nbsp;&nbsp;
                                            <LoadingSkeleton
                                                height="18"
                                                width="150"
                                                accessibleText="Loading prices"
                                            />
                                        </>
                                    }
                                >
                                    <TourHeroLeadInPrice
                                        displayLowestPayment={
                                            pageConfig.displayLowestPayment
                                        }
                                        displayLowestLeadInPrice={
                                            pageConfig.displayLowestLeadInPrice
                                        }
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </>
                    )}
                </Text>
            </div>
            <div className={styles["tour-hero-summary-section-wrapper"]}>
                <TourHeroSummaryInfo />
                <TourHeroSummaryButtons />
            </div>
        </div>
    );
};
